/**
 * normalizeContactPhone — the contact form's phone value as it is submitted.
 *
 * `<ContactForm>`'s `phone` field is phone-ui's `<PhoneInput>`, which hands back E.164 once the
 * number is valid and the formatted partial while it is not (so a half-typed number is never
 * silently dropped). A consumer's validator awaits this to gate the partial and to submit E.164:
 *
 *   - blank (absent, null, whitespace) → `''`: the phone is optional;
 *   - a valid number in any shape      → its E.164 form;
 *   - anything else                    → `null`: the validator names the field.
 *
 * ASYNC on purpose (PERF-FECHUNK-001): the parser is libphonenumber, loaded by a dynamic import
 * only when there is a phone to judge, so it never ships in a page's eager bundle. The chunk it
 * loads is the one `<PhoneInput>` already loaded to render the field.
 *
 * It lives here, not in each app, because the marketing portals reach phone handling only through
 * this package.
 *
 * @module @puffin-core/marketing-shell/contact/normalizeContactPhone
 */

/**
 * @param {string|null|undefined} value - The form's phone value.
 * @returns {Promise<string|null>} `''`, an E.164 number, or `null` when the value is not a valid
 *   number.
 */
export default async function normalizeContactPhone(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  const { toE164 } = await import('@puffin-core/phone-ui');
  return toE164(trimmed);
}
